import React, { FC, useEffect } from 'react'
import Head from 'next/head'
import type { GoogleTagManagerClient } from '@moonpig/web-core-types'
import { useAnalytics } from '@moonpig/web-core-stores'
import {
  CookieCategory,
  setBrowserCookie,
} from '@moonpig/web-core-browser-storage'
import { PageLoadEventFields } from './types'
import { useTrackScreenView } from './useTrackScreenView'
import { EventObjectSchema } from './schemaTypes'
import { errorFormatter } from './utils'
import { initialiseLaunchpadAnalytics } from './initialiseLaunchpadAnalytics'

const createScript = ({
  containerId,
  host,
}: {
  containerId: string
  host: string
}) => {
  return {
    gtmScript: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(), event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://${host}/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${containerId}');
    `,
    noScriptIframeSource: `https://${host}/ns.html?id=${containerId}`,
  }
}

const createStapeScript = ({
  stapeContainerId,
  stapeJsFilename,
}: {
  stapeJsFilename: string
  stapeContainerId: string
}) => {
  return {
    gtmScript: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s);j.async=true;j.src=
    "${getStapePath(stapeJsFilename)}?"+i;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','1i=${stapeContainerId}');
    `,
    noScriptIframeSource: ``,
  }
}

const getStapePath = (stapeJsFilename: string) => {
  if (process.env.MNPG_ENVIRONMENT_NAME === 'local') {
    return `https://www.moonpig.com/moonlog/${stapeJsFilename}`
  }
  return `/moonlog/${stapeJsFilename}`
}

type PageTracking = {
  type: string
  disableDefaultPageTracking?: boolean
  sortby?: string
} & PageLoadEventFields

export const SetupGTM: FC<{
  pageTracking: PageTracking
  pageRequiresLogin: boolean
  enableThirdPartyScripts?: boolean
  validationEnabled?: boolean
  stapeEnabled: boolean
}> = ({
  pageTracking,
  pageRequiresLogin,
  enableThirdPartyScripts,
  validationEnabled,
  stapeEnabled,
}) => {
  const analytics = useAnalytics()

  useEffect(() => {
    initialiseLaunchpadAnalytics()

    if (typeof window !== 'undefined') {
      /* istanbul ignore next */
      window.validateEventPayload = (event: Record<string, unknown>) => {
        const validationResult = EventObjectSchema.safeParse(event)

        if (validationResult.success) return true

        const errorTable = validationResult.error.issues.map(errorFormatter)

        // eslint-disable-next-line no-console
        console.table(errorTable, [
          'key',
          'error_type',
          'criteria',
          'expected',
          'received',
        ])

        return errorTable
      }

      /* istanbul ignore next */
      window.validateDataLayer = () => {
        window.dataLayer
          .filter(e => !!e.event)
          .forEach(e => {
            // eslint-disable-next-line no-console
            console.log(
              `Original Payload: \n${JSON.stringify(
                e,
                null,
                2,
              )} \n\nValidation Result:\n${JSON.stringify(
                window.validateEventPayload?.(e),
                null,
                2,
              )}`,
            )
          })
      }
    }

    return () => {
      window.validateEventPayload = undefined
      window.validateDataLayer = undefined
    }
  }, [])

  useEffect(() => {
    setBrowserCookie(
      'analytics-debug',
      validationEnabled ? '1' : '0',
      {
        path: '/',
      },
      CookieCategory.STRICTLY_NECESSARY,
    )
  }, [validationEnabled])

  const settings: GoogleTagManagerClient = analytics.gtmClient

  useTrackScreenView({
    ...pageTracking,
    pageRequiresLogin,
  })

  const { gtmScript, noScriptIframeSource } = stapeEnabled
    ? createStapeScript({
        stapeContainerId: settings.stapeContainerId,
        stapeJsFilename: settings.stapeJsFilename,
      })
    : createScript({
        containerId: settings.containerId,
        host: settings.host,
      })

  // We only want to inject script if we have a correct currentContainerId
  return settings.containerId && enableThirdPartyScripts ? (
    <>
      <Head>
        {/* eslint-disable react/no-danger */}
        <script
          dangerouslySetInnerHTML={{
            __html: `window.dataLayer = window.dataLayer || []; ${gtmScript}`,
          }}
          data-testid="gtm-script"
        />
        {/* Google Webmaster Tools site verification code */}
        {'verificationCode' in settings ? (
          /* istanbul ignore next */ <meta
            content={settings.verificationCode}
            name="google-site-verification"
          />
        ) : null}
      </Head>

      {noScriptIframeSource ? (
        <noscript>
          <iframe
            src={noScriptIframeSource}
            style={{ display: 'none', visibility: 'hidden' }}
            title="googleTagManager"
          />
        </noscript>
      ) : null}
    </>
  ) : null
}
